import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

// Landing is the LCP page — keep it eagerly loaded so first paint is instant.
import Landing from "@/pages/Landing";
import { initAnalytics, track } from "@/lib/analytics";
import { captureReferral } from "@/lib/referral";

// Every other route is lazy-split so Landing visitors don't download
// AnalyzeFlow, Report, Admin, Blog, Legal, etc. Cuts initial bundle ~60%.
const AnalyzeFlow            = lazy(() => import("@/pages/AnalyzeFlow"));
const Report                 = lazy(() => import("@/pages/Report"));
const Legal                  = lazy(() => import("@/pages/Legal"));
const Admin                  = lazy(() => import("@/pages/Admin"));
const SampleRedirect         = lazy(() => import("@/pages/SampleRedirect"));
const InstagramAuditFramework = lazy(() => import("@/pages/InstagramAuditFramework"));
const Blog                   = lazy(() => import("@/pages/Blog"));
const BlogPost               = lazy(() => import("@/pages/BlogPost"));
const ReferralDashboard      = lazy(() => import("@/pages/ReferralDashboard"));
const ManageSubscription     = lazy(() => import("@/pages/ManageSubscription"));

function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    track("$pageview", { path: loc.pathname });
  }, [loc.pathname]);
  useEffect(() => {
    captureReferral(loc.search);
  }, [loc.search]);
  return null;
}

// Tiny minimal fallback while a lazy chunk fetches — visually consistent with
// the app's dark theme, no CLS, no jarring flash.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-[#0a0b12] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-white/20 border-t-[#FF4D8D] rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    // Defer PostHog init until browser is idle to protect FCP/LCP
    const start = () => initAnalytics();
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 4000 });
    } else {
      setTimeout(start, 2500);
    }
  }, []);
  return (
    <BrowserRouter>
      <RouteTracker />
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "rgba(23,25,35,0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            color: "white",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/analyze" element={<AnalyzeFlow />} />
          <Route path="/report/:id" element={<Report />} />
          <Route path="/sample" element={<SampleRedirect />} />
          <Route path="/instagram-audit-framework" element={<InstagramAuditFramework />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/legal/:kind" element={<Legal />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/referral/:code" element={<ReferralDashboard />} />
          <Route path="/subscription/:id" element={<ManageSubscription />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
